import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from '@/shared/config/routes';
import { track } from '@/shared/lib/analytics';
import { readLinkToken } from '../model/link-token';
import type { SignInLinkData } from '../types';
import { useSignInWithLink } from './use-sign-in-with-link';

export type LinkSignInState =
  | { name: 'checking' }
  | { name: 'invalid' }
  | { name: 'expired'; link: SignInLinkData }
  | { name: 'sent'; link: SignInLinkData; isResent: boolean };

type UseLinkSignInResult = {
  state: LinkSignInState;
  showSent: (link: SignInLinkData, isResent: boolean) => void;
};

/** Uses the token in the fragment exactly once, then drops it from the address bar. */
export function useLinkSignIn(): UseLinkSignInResult {
  const location = useLocation();
  const navigate = useNavigate();
  const { signInWithLink } = useSignInWithLink();
  const [state, setState] = useState<LinkSignInState>({ name: 'checking' });
  const [token] = useState(() => readLinkToken(location.hash));
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    if (!token) {
      setState({ name: 'invalid' });
      return;
    }
    navigate({ pathname: location.pathname, hash: '' }, { replace: true });
    void signInWithLink(token)
      .then((outcome) => {
        if (outcome.status === 'signed-in') {
          track('login', { method: 'email' });
          navigate(outcome.hasWorkspace ? ROUTES.OVERVIEW : ROUTES.WELCOME, { replace: true });
        } else if (outcome.status === 'expired') {
          setState({ name: 'expired', link: outcome.link });
        } else {
          setState({ name: 'invalid' });
        }
      })
      .catch(() => setState({ name: 'invalid' }));
  }, [location.pathname, navigate, signInWithLink, token]);

  return {
    state,
    showSent: (link, isResent) => setState({ name: 'sent', link, isResent }),
  };
}
